import { Order } from 'src/utils/interfaces/interfaces';
import { useGetOrderEventsByIdQuery } from 'src/services/orders/orders';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { t } from 'i18next';

export interface IVehicleLogProps {
  Order: Order;
}

export function VehicleLog({ Order }: IVehicleLogProps) {
  const { id } = useParams();

  const isInTransit = Order?.status === "in_transit";

  const { data: Events } = useGetOrderEventsByIdQuery(id ? id : '', {
    skip: !id,
    pollingInterval: isInTransit ? 30000 : 0,
  });

  const Danger = '/assets/icons/orders/issues/danger.svg';
  const Success = '/assets/icons/orders/issues/success.svg';
  const [eventIsArray, setEventIsArray] = useState(false);

  useEffect(() => {
    let isArray = false;

    if (Array.isArray(Events)) {
      for (let event of Events) {
        if (Array.isArray(event.events)) {
          isArray = true;
          break;
        }
      }
    }

    setEventIsArray(isArray);
  }, [Events]);

  const formatString = (str: string): string => {
    let result = str.replace(/_/g, ' '); // replace underscores with spaces
    result = result.charAt(0).toUpperCase() + result.slice(1); // capitalize the first character
    if (result.includes('rfid')) {
      // convert "rfid" to "RFID"
      result = result.replace(/rfid/g, 'RFID');
    }
    return result;
  };

  return (
    <div style={{ maxHeight: '85vh', overflowY: 'scroll' }}>
      {eventIsArray || Events?.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 h-[50vh]">
          <h3 className="mb-2 text-xl font-medium tracking-wide text-center" style={{ color: 'var(--text-muted)' }}>
            {t('order_details.no_events')}
          </h3>
          <div className="w-[100px] mt-2 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent opacity-50"></div>
        </div>
      ) : (
        Events?.map((event: any, index: any) => {
          // Collect event content
          const eventContent = [];

          if (event.events.swipe_rfid_card) {
            eventContent.push(
              <div className="grid grid-cols-12 gap-2 mb-6 px-7" key="swipe_rfid_card">
                <div className="col-span-2">
                  <img
                    src={
                      event.events.swipe_rfid_card === 'illegal_swipe' ||
                      event.events.swipe_rfid_card === 'illegal_rfid_card_swipe'
                        ? Danger
                        : Success
                    }
                    className="mx-auto"
                    alt="Swipe RFID Card Event"
                  />
                </div>
                <div className="col-span-10">
                  <p className="text-base">{formatString(event.events.swipe_rfid_card)}</p>
                  <p className="text-xs text-blue/[0.5]">
                    {moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>
            );
          }

          if (event.events.lock_rope_pull_out) {
            eventContent.push(
              <div className="grid grid-cols-12 gap-2 mb-6 px-7" key="lock_rope_pull_out_true">
                <div className="col-span-2">
                  <img
                    src="/assets/icons/orders/events/unlock.svg"
                    className="mx-auto"
                    alt="Rope Pulled Out Event"
                  />
                </div>
                <div className="col-span-10">
                  <p className="text-base">Rope pulled out</p>
                  <p className="text-xs text-blue/[0.5]">
                    {moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>
            );
          }

          if (event.events.lock_rope_pull_out === false) {
            eventContent.push(
              <div className="grid grid-cols-12 gap-2 mb-6 px-7" key="lock_rope_pull_out_false">
                <div className="col-span-2">
                  <img
                    src="/assets/icons/orders/events/lock.svg"
                    className="mx-auto"
                    alt="Rope Inserted Event"
                  />
                </div>
                <div className="col-span-10">
                  <p className="text-base">Rope inserted</p>
                  <p className="text-xs text-blue/[0.5]">
                    {moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>
            );
          }

          if (event.events.rope_cut_alarm) {
            eventContent.push(
              <div className="grid grid-cols-12 gap-2 mb-6 px-7" key="rope_cut_alarm">
                <div className="col-span-2">
                  <img src={Danger} className="mx-auto" alt="Rope Cut Alarm" />
                </div>
                <div className="col-span-10">
                  <p className="text-base">Rope cut</p>
                  <p className="text-xs text-blue/[0.5]">
                    {moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>
            );
          }

          if (event.events.back_cover_opened_alarm) {
            eventContent.push(
              <div className="grid grid-cols-12 gap-2 mb-6 px-7" key="back_cover_opened_alarm">
                <div className="col-span-2">
                  <img src={Success} className="mx-auto" alt="Back Cover Opened" />
                </div>
                <div className="col-span-10">
                  <p className="text-base">Back cover opened</p>
                  <p className="text-xs text-blue/[0.5]">
                    {moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>
            );
          }

          // Handle route_violation for specific cases
          if (
            event.events.route_violation === 'device_in_origin' ||
            event.events.route_violation === 'device_in_destination'
          ) {
            eventContent.push(
              <div className="grid grid-cols-12 gap-2 mb-6 px-7" key="route_violation">
                <div className="col-span-2">
                  <img src={Success} className="mx-auto" alt="Route Violation Event" />
                </div>
                <div className="col-span-10">
                  <p className="text-base">{formatString(event.events.route_violation)}</p>
                  <p className="text-xs text-blue/[0.5]">
                    {moment(event.timestamp).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>
            );
          }

          // Only render the outer div and hr if there's content
          if (eventContent.length > 0) {
            return (
              <div className="mb-6" key={index}>
                {eventContent}
                <hr className="opacity-10" />
              </div>
            );
          } else {
            return null; // Don't render anything if there's no content
          }
        })
      )}
    </div>
  );
}
